import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import {
    createEmailTemplate,
    updateEmailTemplate,
} from '../services/TemplatesService';
import { toast } from 'react-toastify';
import { useHtmlEditor } from '../hooks/useHtmlEditor';
import { QUILL_MODULES, QUILL_FORMATS } from '../constants/quillConfig';
import { initializeQuillPolyfill } from '../utils/quillPolyfill';
import 'react-quill/dist/quill.snow.css';

// Initialize Quill polyfill
initializeQuillPolyfill();

function TemplateSidebar({ isOpen, onClose, template, onSaved }) {
    const isEdit = !!template;
    const [templateName, setTemplateName] = useState(template?.templateName || '');
    const [subject, setSubject] = useState(template?.subject || '');
    const [saving, setSaving] = useState(false);
    const {
        editorMode,
        richTextContent,
        htmlContent,
        setRichTextContent,
        setHtmlContent,
        formatHtml,
        handleModeSwitch,
        getActiveContent,
        initializeContent,
    } = useHtmlEditor();

    useEffect(() => {
        setTemplateName(template?.templateName || '');
        setSubject(template?.subject || '');

        const body = template?.body || '';
        initializeContent(body);
    }, [template, initializeContent]);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const htmlBody = getActiveContent();
            if (isEdit) {
                await updateEmailTemplate(template.id, {
                    templateName,
                    subject,
                    body: htmlBody,
                });
                toast.success('Template updated');
            } else {
                await createEmailTemplate({
                    templateName,
                    subject,
                    body: htmlBody,
                });
                toast.success('Template created');
            }
            onSaved();
        } catch (err) {
            toast.error('Failed to save template');
        }
        setSaving(false);
    }

    return (
        <div className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isOpen ? 'd-none' : ''}`}
            style={{ width: '800px', zIndex: 1045 }}>
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{isEdit ? 'Edit Template' : 'Create Template'}</h5>
                <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="p-3">
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Template Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            value={templateName}
                            onChange={e => setTemplateName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Subject *</label>
                        <input
                            type="text"
                            className="form-control"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-semibold mb-0">Body *</label>
                            <div className="btn-group btn-group-sm" role="group">
                                <input 
                                    type="radio" 
                                    className="btn-check" 
                                    name="editorMode" 
                                    id="richEditor" 
                                    checked={editorMode === 'rich'} 
                                    onChange={() => handleModeSwitch('rich')}
                                />
                                <label className="btn btn-outline-primary" htmlFor="richEditor">Rich Text</label>
                                
                                <input 
                                    type="radio" 
                                    className="btn-check" 
                                    name="editorMode" 
                                    id="htmlEditor" 
                                    checked={editorMode === 'html'} 
                                    onChange={() => handleModeSwitch('html')}
                                />
                                <label className="btn btn-outline-primary" htmlFor="htmlEditor">HTML</label>
                            </div>
                        </div>
                        
                        {editorMode === 'rich' ? (
                                <ReactQuill
                                    theme="snow"
                                    value={richTextContent}
                                    onChange={setRichTextContent}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                />
                        ) : (
                            <div>
                                <textarea
                                    className="form-control"
                                    value={htmlContent}
                                    onChange={(e) => setHtmlContent(formatHtml(e.target.value))}
                                    rows={18}
                                    placeholder={'Enter your HTML email template here...'}
                                    style={{
                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                        fontSize: '13px',
                                        lineHeight: '1.4',
                                        tabSize: 2
                                    }}
                                    required
                                />
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary px-3" 
                            onClick={onClose} 
                            disabled={saving}
                            style={{ minWidth: '80px' }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn btn-primary px-3" 
                            disabled={saving}
                            style={{ minWidth: '80px' }}
                        >
                            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default TemplateSidebar;
